import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import SEO from "../components/SEO.jsx";

const css = `
:root {
  --ps-accent:#6E5BFF;
  --ps-accent-2:#A78BFA;
  --ps-black:#0b0b0c;
  --ps-muted:#6b7280;
  --nav-h: 56px; /* Výška navbaru z App.jsx */
}

/* 1. Skryjeme defaultní scrollbar na body */
body { overflow: hidden; }

/* 2. Custom Scrollbar Container (stejný jako Home.jsx) */
.page-scroll {
  height: calc(100vh - var(--nav-h));
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #a78bfa #eef1ff;
  
  /* 3. Flexbox pro Sticky Footer */
  display: flex;
  flex-direction: column;
}

.page-scroll::-webkit-scrollbar { width: 10px; }
.page-scroll::-webkit-scrollbar-track {
  background:#eef1ff;
  border-left:1px solid rgba(17,24,39,.08);
}
.page-scroll::-webkit-scrollbar-thumb {
  background:linear-gradient(180deg,#c7d2fe,#a78bfa);
  border:2px solid #eef1ff;
  border-radius:8px;
}

.blog-idx-wrap {
  /* Flex grow, aby obsah roztáhl stránku a odtlačil footer */
  flex: 1 0 auto; 
  background: radial-gradient(1200px 600px at 50% -10%, #f4f6ff, #f7f9fc 40%, #fff);
  padding-bottom: 40px;
}

.idx-container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 80px 20px 40px;
}

.idx-header { text-align: center; margin-bottom: 60px; }
.idx-title { font-size: 48px; font-weight: 800; letter-spacing: -0.02em; color: var(--ps-black); margin-bottom: 16px; }
.idx-sub { font-size: 18px; color: var(--ps-muted); max-width: 600px; margin: 0 auto; }

.idx-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 40px;
}

/* Card Style */
.blog-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid rgba(17, 24, 39, .08);
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
  display: flex;
  flex-direction: column;
  text-decoration: none;
  height: 100%;
}
.blog-card:hover { transform: translateY(-4px); box-shadow: 0 12px 24px rgba(0,0,0,0.06); border-color: rgba(110,91,255,0.3); }
.card-body { padding: 24px; flex: 1; display: flex; flex-direction: column; }
.card-cat { font-size: 11px; font-weight: 700; text-transform: uppercase; color: var(--ps-accent); margin-bottom: 12px; letter-spacing: 0.05em; }
.card-title { font-size: 20px; font-weight: 700; color: var(--ps-black); margin: 0 0 12px; line-height: 1.3; }
.card-excerpt { font-size: 14px; color: var(--ps-muted); line-height: 1.6; margin-bottom: 20px; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.card-meta { margin-top: auto; font-size: 13px; color: #9ca3af; display: flex; align-items: center; gap: 8px; }

/* Footer - Flex shrink 0 aby se nezmenšoval */
.ps-footer {
  flex-shrink: 0;
  border-top: 1px solid rgba(148,163,184,0.35);
  padding: 30px 20px;
  background: #fff; /* Aby footer nebyl průhledný na konci stránky */
}
.footer-container { max-width: 1000px; margin: 0 auto; }
.ps-footer-inner { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.ps-footer-brand { display: flex; align-items: center; gap: 10px; }
.ps-footer-logo { width: 26px; height: 26px; display: inline-flex; align-items: center; justify-content: center; }
.ps-footer-title { font-weight: 700; font-size: 15px; color: var(--ps-black); }
.ps-footer-brand p { margin: 0; font-size: 13px; color: #9ca3af; }
.ps-footer-links { display: flex; flex-wrap: wrap; gap: 10px; font-size: 13px; }
.ps-footer-links a { text-decoration: none; color: #6b7280; }
.ps-footer-links a:hover { color: var(--ps-black); text-decoration: underline; }
.ps-footer-bottom { margin-top: 12px; font-size: 12px; color: #9ca3af; text-align: left; }
`;

export default function BlogIndex() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Lock body scroll to force usage of .page-scroll
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    apiFetch("/api/blog/posts")
      .then((data) => setPosts(data.items || []))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <SEO 
        title="MusicBase Blog - Music Marketing Tips" 
        description="Guides on Spotify promotion, playlist pitching, and music marketing strategies."
      />
      <style>{css}</style>
      
      {/* Scrollable Container */}
      <div className="page-scroll">
        
        {/* Main Content (Flex Grow) */}
        <div className="blog-idx-wrap">
          <div className="idx-container">
            <header className="idx-header">
              <h1 className="idx-title">Latest Updates</h1>
              <p className="idx-sub">
                Tips, strategies, and guides to help you promote your music and grow your fanbase on Spotify.
              </p>
            </header>

            {loading ? (
              <div style={{textAlign:'center', padding:'40px'}}>Loading...</div>
            ) : (
              <div className="idx-grid">
                {posts.map((post) => (
                  <Link to={`/blog/${post.slug}`} key={post.id} className="blog-card">
                    <div className="card-body">
                      <div className="card-cat">{post.category}</div>
                      <h2 className="card-title">{post.title}</h2>
                      <p className="card-excerpt">{post.excerpt}</p>
                      
                      <div className="card-meta">
                        <span>{new Date(post.published_at || post.created_at).toLocaleDateString()}</span>
                        <span>•</span>
                        <span>{post.author}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
            
            {!loading && posts.length === 0 && (
              <div style={{textAlign:'center', color:'#6b7280'}}>No posts found yet.</div>
            )}
          </div>
        </div>

        {/* Footer (Fixed at bottom if content is short) */}
        <footer className="ps-footer">
          <div className="footer-container">
            <div className="ps-footer-inner">
              <div className="ps-footer-brand">
                <span className="ps-footer-logo" aria-hidden>
                  <img
                    className="brand-icon"
                    src="https://www.musicbase.io/logo.png"
                    alt=""
                    aria-hidden
                    style={{ height: "25px", width: "25px" }}
                  />
                </span>
                <div>
                  <div className="ps-footer-title">MusicBase</div>
                  <p>Playlist outreach built for artists, not sales teams.</p>
                </div>
              </div>

              <nav className="ps-footer-links" aria-label="Footer">
                <a href="/">Home</a>
                <a href="/pricing">Pricing</a>
                <a href="/login">Log in</a>
                <a href="/signup">Sign up</a>
                <a href="/contact">Contact</a>
                <a href="/terms">Terms</a>
                <a href="/privacy">Privacy Policy</a>
              </nav>
            </div>
            <div className="ps-footer-bottom">
              © {new Date().getFullYear()} MusicBase. All rights reserved.
            </div>
          </div>
        </footer>

      </div>
    </>
  );
}